import React, { useMemo, useState } from 'react'
import {
  Box, Chip, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Typography,
} from '@mui/material'
import TrendingUpIcon   from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import RemoveIcon       from '@mui/icons-material/Remove'
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined'
import BarChartIcon     from '@mui/icons-material/BarChart'
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_MID, WARNING, WARNING_LIGHT, GREEN, RED, matchRow, scopeTotals, displayName, Kpi, Pct } from './index'

const nf = n => (n == null || isNaN(n)) ? '—' : Math.round(n).toLocaleString('en-IN')

function SegBadge({ seg }) {
  return (
    <Box component='span' sx={{ fontSize: '0.6rem', fontWeight: 800, px: 0.8, py: 0.25, borderRadius: 0.5, letterSpacing: 0.3, background: seg === 'ENT' ? PRIMARY_LIGHT : WARNING_LIGHT, color: seg === 'ENT' ? PRIMARY : WARNING }}>
      {seg}
    </Box>
  )
}

function Th({ children, col, sortCol, sortDir, onSort, right }) {
  const active = sortCol === col
  return (
    <TableCell align={right ? 'right' : 'left'} onClick={col ? () => onSort(col) : undefined} sx={{
      fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: active ? PRIMARY : '#a0a0b8',
      background: '#F4F6F8', borderBottom: `2px solid ${PRIMARY_MID}`, py: 1.25, whiteSpace: 'nowrap',
      cursor: col ? 'pointer' : 'default', position: 'sticky', top: 0, zIndex: 2,
      '&:hover': col ? { color: PRIMARY } : {},
    }}>
      {children}{active && <span style={{ fontSize: 8, marginLeft: 3 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>}
    </TableCell>
  )
}

/* Rank badge — medal colours for top 3 */
function Rank({ n }) {
  const gold   = { background: 'linear-gradient(135deg, #f59e0b, #fbbf24)', color: '#fff', boxShadow: '0 2px 6px #f59e0b44' }
  const silver = { background: 'linear-gradient(135deg, #9ca3af, #d1d5db)', color: '#fff', boxShadow: '0 2px 6px #9ca3af44' }
  const bronze = { background: 'linear-gradient(135deg, #cd7c3a, #e0924d)', color: '#fff', boxShadow: '0 2px 6px #cd7c3a44' }
  const plain  = { background: PRIMARY_LIGHT, color: PRIMARY }
  const style  = n === 1 ? gold : n === 2 ? silver : n === 3 ? bronze : plain
  return (
    <Box sx={{ width: 26, height: 26, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.65rem', fontWeight: 800, flexShrink: 0, ...style }}>
      {n}
    </Box>
  )
}

function TrendBadge({ v }) {
  if (v > 0) return <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: GREEN, fontWeight: 700, fontSize: '0.72rem' }}><TrendingUpIcon sx={{ fontSize: 14 }} />Growing</Box>
  if (v < 0) return <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: RED,   fontWeight: 700, fontSize: '0.72rem' }}><TrendingDownIcon sx={{ fontSize: 14 }} />Declining</Box>
  return         <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4, color: '#bbb', fontSize: '0.72rem' }}><RemoveIcon sx={{ fontSize: 14 }} />Flat</Box>
}

const COHORT_SIZE = 50

export default function P5Top50({ vm, filters, L0, L1, L2, S0, S1, S2 }) {
  const [sortCol, setSortCol] = useState('tot3')
  const [sortDir, setSortDir] = useState('desc')

  // WHO is in the table — the actual top 50 by 3-month total, and nothing else.
  // Membership deliberately does NOT depend on the display sort: this used to
  // sort the full account list by whatever column was active and only then
  // slice(0, 50), so sorting ascending swapped the cohort for the 50 SMALLEST
  // accounts (all zeros) and dragged every KPI to 0 with it. Rank is assigned
  // here so it stays the account's true standing when the table is re-sorted.
  const cohort = useMemo(() => {
    const base = (vm.overview.all || [])
      .filter(r => matchRow(r, filters))
      .map(r => ({ ...r, tot3: (r.apr || 0) + (r.may || 0) + (r.jun || 0) }))
    return [...base]
      .sort((a, b) => (b.tot3 || 0) - (a.tot3 || 0))
      .slice(0, COHORT_SIZE)
      .map((r, i) => ({ ...r, rank: i + 1 }))
  }, [vm, filters])

  // ORDER the cohort is displayed in — reorders those 50 rows, never changes
  // which 50 they are.
  const rows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1
    return [...cohort].sort((a, b) => {
      const va = a[sortCol], vb = b[sortCol]
      // name/kam/sam are strings; numeric subtraction on them yields NaN, which
      // leaves the rows in arbitrary order instead of sorting them.
      if (typeof va === 'string' || typeof vb === 'string') {
        return dir * String(va ?? '').localeCompare(String(vb ?? ''))
      }
      return dir * ((va ?? -Infinity) - (vb ?? -Infinity))
    })
  }, [cohort, sortCol, sortDir])

  function toggleSort(col) { if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortCol(col); setSortDir('desc') } }

  const thP = { sortCol, sortDir, onSort: toggleSort }
  // KPIs describe the cohort, so they read off `cohort` — never off the
  // display-sorted list, which is what made them swing to 0 on an ascending sort.
  const growing   = cohort.filter(r => r.gMJ > 0).length
  const degrowing = cohort.filter(r => r.gMJ < 0).length
  const sumCur    = cohort.reduce((s, r) => s + (r.jun || 0), 0)
  const sumTot    = cohort.reduce((s, r) => s + (r.tot3 || 0), 0)

  return (
    <Box>
      <Grid container spacing={1.75} sx={{ mb: 2.5 }}>
        {[
          { label: `Top 50 · ${L2} shipments`, value: nf(sumCur),    sub: 'month-to-date',          color: PRIMARY, icon: BarChartIcon },
          { label: '3-month total',             value: nf(sumTot),    sub: `${L0}–${L2}`,            color: '#555' },
          { label: 'Growing',                   value: nf(growing),   sub: 'of top 50 accounts',     color: GREEN,  icon: TrendingUpIcon },
          { label: 'De-growing',                value: nf(degrowing), sub: 'of top 50 accounts',     color: RED,    icon: TrendingDownIcon },
        ].map((k, i) => (
          <Grid item xs={6} sm={3} key={i}><Kpi {...k} /></Grid>
        ))}
      </Grid>

      <Box sx={{ mb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
          <Box sx={{ width: 4, height: 16, borderRadius: 1, background: PRIMARY }} />
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a2e' }}>Top 50 Clients</Typography>
          <Chip label='Click column to re-sort' size='small' sx={{ height: 18, fontSize: '0.58rem', fontWeight: 600, background: PRIMARY_LIGHT, color: PRIMARY, ml: 0.5 }} />
        </Box>
        <Typography sx={{ fontSize: '0.7rem', color: '#9a9ab0', ml: 1.25 }}>
          Ranked by 3-month total. Growth compares <strong>daily run rate</strong> ({L2} vs {L1}).
          🥇🥈🥉 — top 3 accounts.
        </Typography>
      </Box>

      <Box sx={{ background: '#fff', borderRadius: '12px', border: '1px solid rgba(145,158,171,0.24)', boxShadow: 'none', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 680 }}>
          <Table size='small' stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', color: '#a0a0b8', background: '#F4F6F8', borderBottom: `2px solid ${PRIMARY_MID}`, py: 1.25, width: 48, textAlign: 'center' }}>#</TableCell>
                <Th {...thP} col='name'  minW={180}>Account</Th>
                <Th>Seg</Th>
                <Th {...thP} col='kam'>KAM</Th>
                <Th {...thP} col='sam'>SAM</Th>
                <Th {...thP} col='apr'  right>{L0}</Th>
                <Th {...thP} col='may'  right>{L1}</Th>
                <Th {...thP} col='jun'  right>{L2} MTD</Th>
                <Th {...thP} col='tot3' right>3-mo Total</Th>
                <Th {...thP} col='gMJ'  right>{S1}→{S2}</Th>
                <Th>Trend</Th>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Keyed by oid, not name: merchant names are not unique, and the
                  duplicate React keys that caused left ghost rows behind on re-sort. */}
              {rows.map(r => (
                <TableRow key={r.oid || r.name} hover sx={{ background: r.gMJ < 0 ? '#fff8f8' : 'inherit', '&:hover td': { background: r.gMJ < 0 ? '#FFE7D9' : '#F9FAFB' } }}>
                  <TableCell sx={{ textAlign: 'center', py: 0.9 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Rank n={r.rank} />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e', py: 0.9, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</TableCell>
                  <TableCell sx={{ py: 0.9 }}><SegBadge seg={r.seg} /></TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: '#9a9ab0', py: 0.9, whiteSpace: 'nowrap' }}>{r.kam}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', color: '#9a9ab0', py: 0.9, whiteSpace: 'nowrap' }}>{displayName(r.sam)}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.7rem', color: '#bbb', py: 0.9 }}>{nf(r.apr)}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.75rem', py: 0.9 }}>{nf(r.may)}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.82rem', fontWeight: 700, color: PRIMARY, py: 0.9 }}>{nf(r.jun)}</TableCell>
                  <TableCell align='right' sx={{ fontSize: '0.82rem', fontWeight: 700, py: 0.9 }}>{nf(r.tot3)}</TableCell>
                  <TableCell align='right' sx={{ py: 0.9 }}><Pct v={r.gMJ} /></TableCell>
                  <TableCell sx={{ py: 0.9 }}><TrendBadge v={r.gMJ} /></TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={11} align='center' sx={{ py: 6, color: '#bbb', fontSize: '0.82rem' }}>No accounts match the current filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  )
}
